import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Truck, AlertTriangle, Package, Wrench, FileText, Beaker, Plus, Search, X, XCircle } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Skeleton } from '../../components/ui/Skeleton';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { toast } from '../../components/ui/Toast';
import { usePedidoDetail, useUpdatePedidoItems, useOrdenMaterialesPdf, useAddItemAdicional, useDeletePedidoItem, useCancelPedido } from '../../hooks/usePedidos';
import { useMaterialBalance } from '../../hooks/useMaterialBalance';
import { useEquipos } from '../../hooks/useEquipos';
import { useCreateDespacho } from '../../hooks/useDespachos';
import { useProjectMaestros } from '../../hooks/useProjects';
import { useInsumos } from '../../hooks/useInsumos';
import { useMaterials } from '../../hooks/useMaterials';
import { useAuthStore } from '../../stores/auth-store';
import type { MaterialBalanceItem } from '../../hooks/useMaterialBalance';

const CATEGORY_LABELS: Record<string, string> = {
  gypsum: 'Gypsum',
  pintura: 'Pintura',
  impermeabilizacion: 'Impermeab.',
  empaste: 'Empaste',
  general: 'General',
};

// mdo intentionally excluded — labor costs are not physical materials to order/dispatch
const CATEGORY_ORDER = ['gypsum', 'pintura', 'impermeabilizacion', 'empaste', 'general'];

type MainTab = 'materiales' | 'equipos' | 'insumos';

export function ComprasPedidoDetailPage() {
  const { pedidoId } = useParams<{ pedidoId: string }>();
  const navigate = useNavigate();

  const { data: pedido, isLoading: loadingPedido } = usePedidoDetail(pedidoId ?? '');
  const { data: balance, isLoading: loadingBalance } = useMaterialBalance(pedido?.project_id ?? '');
  const { data: inventario, isLoading: loadingEquipos } = useEquipos();
  const { data: maestrosDelProyecto } = useProjectMaestros(pedido?.project_id ?? '');
  const updateItems = useUpdatePedidoItems();
  const ordenPdf = useOrdenMaterialesPdf();
  const addItem = useAddItemAdicional();
  const deleteItem = useDeletePedidoItem();
  const cancelPedido = useCancelPedido();
  const { data: insumosData = [] } = useInsumos();
  const { user } = useAuthStore();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const canManageAdicionales =
    user?.role === 'compras' ||
    user?.role === 'administrativo' ||
    user?.role === 'operaciones' ||
    user?.role === 'gerente';

  const [mainTab, setMainTab] = useState<MainTab>('materiales');
  const [materialQty, setMaterialQty] = useState<Map<string, number>>(new Map());
  const [equipoQty, setEquipoQty] = useState<Map<string, number>>(new Map());
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [showDispatch, setShowDispatch] = useState(false);
  const [saved, setSaved] = useState(false);
  const [selectedMaestroId, setSelectedMaestroId] = useState<string>('');
  const [excessModal, setExcessModal] = useState<{
    materialId: string;
    nombre: string;
    newQty: number;
    pendiente: number;
  } | null>(null);
  const [excessJustification, setExcessJustification] = useState('');
  const [justifications, setJustifications] = useState<Map<string, string>>(new Map());

  // Adicionales modal state (materiales adicionales)
  const [showAddMatAdicional, setShowAddMatAdicional] = useState(false);
  const [addMatId, setAddMatId] = useState('');
  const [addMatName, setAddMatName] = useState('');
  const [addMatSearch, setAddMatSearch] = useState('');
  const [addMatQty, setAddMatQty] = useState(0);
  const { data: matSearchResponse } = useMaterials(
    { search: addMatSearch || undefined, notCategoria: 'mdo', isActive: true, limit: 30 },
  );
  const matSearchResults = matSearchResponse?.data ?? [];

  // Insumos catalog state (full catalog qty-selector, same pattern as equipos)
  const [insumoQty, setInsumoQtyState] = useState<Map<string, number>>(new Map());
  const [insumosSaved, setInsumosSaved] = useState(false);

  const handleCancelPedido = async () => {
    if (!pedidoId) return;
    try {
      await cancelPedido.mutateAsync({ pedidoId });
      toast('success', 'Pedido cancelado');
      setShowCancelModal(false);
      navigate('/despachos');
    } catch {
      toast('error', 'Error al cancelar el pedido');
    }
  };

  // Search state for each tab — reset on tab switch
  const [materialSearch, setMaterialSearch] = useState('');
  const [equipoSearch, setEquipoSearch] = useState('');
  const [insumoSearch, setInsumoSearch] = useState('');

  // Initialize material quantities once data loads
  useEffect(() => {
    if (!pedido || !balance) return;

    const existingMaterialItems = pedido.items.filter(
      (i) => i.material_id && i.material_categoria !== 'mdo' && !i.es_material_adicional,
    );
    const existingEquipoItems = pedido.items.filter((i) => i.equipo_id);

    if (existingMaterialItems.length > 0 || existingEquipoItems.length > 0) {
      // Pre-fill from existing saved items using the REMAINING quantity (total - dispatched).
      // This avoids showing the full ordered amount when part of it has already been dispatched,
      // which would make it appear as if Compras is about to order that amount again.
      const matMap = new Map<string, number>();
      for (const item of existingMaterialItems) {
        if (item.material_id) {
          const remaining = item.cantidad - (item.dispatched_qty ?? 0);
          if (remaining > 0) matMap.set(item.material_id, remaining);
          // remaining <= 0 means fully dispatched → input stays empty (0)
        }
      }
      setMaterialQty(matMap);

      const eqMap = new Map<string, number>();
      for (const item of existingEquipoItems) {
        if (item.equipo_id) {
          const remaining = item.cantidad - (item.dispatched_qty ?? 0);
          if (remaining > 0) eqMap.set(item.equipo_id, remaining);
        }
      }
      setEquipoQty(eqMap);
      setSaved(true);
    }

    // Pre-fill insumo quantities from existing pedido items
    const existingInsumoItems = pedido.items.filter((i) => i.insumo_id);
    if (existingInsumoItems.length > 0) {
      const insMap = new Map<string, number>();
      for (const item of existingInsumoItems) {
        if (item.insumo_id) insMap.set(item.insumo_id, item.cantidad);
      }
      setInsumoQtyState(insMap);
      setInsumosSaved(true);
      setSaved(true);
    }
    // No pre-fill on a fresh pedido — quantities default to 0 (user fills in what they need)
  }, [pedido?.id, balance]);

  // Derive available material categories from balance (no mdo)
  const categories = CATEGORY_ORDER.filter((cat) =>
    (balance ?? []).some((b) => b.material_categoria === cat),
  );

  useEffect(() => {
    if (categories.length > 0 && !activeCategory) {
      setActiveCategory(categories[0]);
    }
  }, [categories.join(',')]);

  const balanceByCategory = (cat: string): MaterialBalanceItem[] =>
    (balance ?? [])
      .filter((b) => b.material_categoria === cat)
      .sort((a, b) => a.material_nombre.localeCompare(b.material_nombre, 'es'));

  const setMatQty = (materialId: string, value: number) => {
    const next = new Map(materialQty);
    if (value <= 0) next.delete(materialId);
    else next.set(materialId, value);
    setMaterialQty(next);
    setSaved(false);
  };

  const handleMatQtyChange = (item: MaterialBalanceItem, value: number) => {
    setMatQty(item.material_id, value);
    if (value <= item.pendiente && justifications.has(item.material_id)) {
      const next = new Map(justifications);
      next.delete(item.material_id);
      setJustifications(next);
    }
  };

  const handleMatQtyBlur = (item: MaterialBalanceItem) => {
    const value = materialQty.get(item.material_id) ?? 0;
    if (value > 0 && item.pendiente >= 0 && value > item.pendiente) {
      setExcessModal({
        materialId: item.material_id,
        nombre: item.material_nombre,
        newQty: value,
        pendiente: item.pendiente,
      });
      setExcessJustification('');
    }
  };

  const handleExcessConfirm = () => {
    if (!excessModal || excessJustification.trim().length < 10) return;
    setMatQty(excessModal.materialId, excessModal.newQty);
    const next = new Map(justifications);
    next.set(excessModal.materialId, excessJustification.trim());
    setJustifications(next);
    setExcessModal(null);
  };

  const setEqQty = (equipoId: string, value: number) => {
    const next = new Map(equipoQty);
    if (value <= 0) next.delete(equipoId);
    else next.set(equipoId, value);
    setEquipoQty(next);
    setSaved(false);
  };

  const setInsumoQty = (insumoId: string, value: number) => {
    const next = new Map(insumoQty);
    if (value <= 0) next.delete(insumoId);
    else next.set(insumoId, value);
    setInsumoQtyState(next);
    setInsumosSaved(false);
  };

  const handleSaveInsumos = async () => {
    if (!pedidoId || !pedido) return;
    const existingByInsumoId = new Map(
      pedido.items.filter((i) => i.insumo_id).map((i) => [i.insumo_id!, i]),
    );

    const toAdd = Array.from(insumoQty.entries())
      .filter(([insumoId, qty]) => qty > 0 && !existingByInsumoId.has(insumoId))
      .map(([insumoId, cantidad]) => ({ insumoId, cantidad }));

    const toDelete = Array.from(existingByInsumoId.entries())
      .filter(([insumoId]) => !insumoQty.has(insumoId) || (insumoQty.get(insumoId) ?? 0) <= 0)
      .filter(([, item]) => (item.dispatched_qty ?? 0) === 0)
      .map(([, item]) => item.id);

    if (toAdd.length === 0 && toDelete.length === 0) {
      toast('info', 'Sin cambios en insumos');
      return;
    }

    try {
      for (const { insumoId, cantidad } of toAdd) {
        await addItem.mutateAsync({ pedidoId, data: { tipo: 'insumo', insumoId, cantidad } });
      }
      for (const itemId of toDelete) {
        await deleteItem.mutateAsync({ itemId, pedidoId });
      }
      toast('success', `Insumos guardados (${toAdd.length} agregados, ${toDelete.length} eliminados)`);
      setInsumosSaved(true);
      setSaved(true);
    } catch {
      // handled by api-client
    }
  };

  const handleSave = async () => {
    if (!pedidoId) return;

    const items: Array<{ materialId?: string; equipoId?: string; cantidad: number }> = [
      ...Array.from(materialQty.entries())
        .filter(([, qty]) => qty > 0)
        .map(([materialId, cantidad]) => ({ materialId, cantidad })),
      ...Array.from(equipoQty.entries())
        .filter(([, qty]) => qty > 0)
        .map(([equipoId, cantidad]) => ({ equipoId, cantidad })),
    ];

    if (items.length === 0) {
      // If there are previously-saved material/equipo items in DB, we must still call the backend
      // so they get deleted. Skipping the call would leave them in DB and the PDF would show them.
      const hasPreviousMaterialEquipo = (pedido?.items ?? []).some(
        (i) => (i.material_id || i.equipo_id) && !i.es_material_adicional,
      );
      if (!hasPreviousMaterialEquipo) {
        // Nothing to sync — allow proceeding if there are adicionales or insumos already saved
        const hasAdicionales = (pedido?.items ?? []).some((i) => i.es_material_adicional);
        const hasInsumos = (pedido?.items ?? []).some((i) => i.insumo_id);
        if (hasAdicionales || hasInsumos) {
          toast('success', 'Pedido guardado');
          setSaved(true);
          return;
        }
        toast('error', 'Debe ingresar al menos una cantidad');
        return;
      }
      // hasPreviousMaterialEquipo=true: fall through to call backend with empty items → deletes them all
    }

    try {
      await updateItems.mutateAsync({ pedidoId, items });
      toast('success', 'Pedido actualizado');
      setSaved(true);
    } catch {
      // handled by api-client
    }
  };

  const totalMatItems = Array.from(materialQty.values()).filter((v) => v > 0).length;
  const totalEqItems = Array.from(equipoQty.values()).filter((v) => v > 0).length;
  const totalItems = totalMatItems + totalEqItems;
  const hasAdicionalesOnly =
    totalItems === 0 && (pedido?.items ?? []).some((i) => i.es_material_adicional);
  const hasInsumosOnly =
    totalItems === 0 && (pedido?.items ?? []).some((i) => i.insumo_id);
  // True when the DB has material/equipo items that the user has zeroed out — Guardar must remain
  // enabled so the backend call runs and removes them (fixes PDF showing stale items).
  const hasPreviousMaterialEquipo =
    totalItems === 0 &&
    (pedido?.items ?? []).some((i) => (i.material_id || i.equipo_id) && !i.es_material_adicional);

  const isLoading = loadingPedido || loadingBalance || loadingEquipos;

  if (isLoading) {
    return (
      <div className="p-4 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (!pedido) {
    return (
      <div className="p-4 text-center">
        <p className="text-red-500">Pedido no encontrado</p>
        <Button variant="secondary" className="mt-4" onClick={() => navigate('/despachos')}>
          Volver
        </Button>
      </div>
    );
  }

  const residenteNota = pedido.notas;
  const customDescriptions = pedido.items.filter((i) => i.nombre_custom);
  const activeEquipos = (inventario ?? [])
    .filter((e) => e.is_active)
    .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));

  // Cross-category search: when materialSearch is active, gather all matching items grouped by category
  const materialSearchTerm = materialSearch.toLowerCase().trim();
  const searchedMaterialGroups = materialSearchTerm
    ? CATEGORY_ORDER.flatMap((cat) => {
        const items = (balance ?? [])
          .filter((b) => b.material_categoria === cat)
          .filter(
            (b) =>
              b.material_nombre.toLowerCase().includes(materialSearchTerm) ||
              (b.material_sku ?? '').toLowerCase().includes(materialSearchTerm),
          )
          .sort((a, b) => a.material_nombre.localeCompare(b.material_nombre, 'es'));
        return items.length > 0 ? [{ cat, items }] : [];
      })
    : null; // null = use normal category filter

  // Equipo search — filter active inventory by name, code, or description
  const filteredEquipos = equipoSearch.trim()
    ? activeEquipos.filter(
        (e) =>
          e.nombre.toLowerCase().includes(equipoSearch.toLowerCase()) ||
          (e.codigo ?? '').toLowerCase().includes(equipoSearch.toLowerCase()) ||
          (e.descripcion ?? '').toLowerCase().includes(equipoSearch.toLowerCase()),
      )
    : activeEquipos;

  const renderMaterialCard = (item: MaterialBalanceItem) => {
    const qty = materialQty.get(item.material_id) ?? 0;
    return (
      <Card key={item.material_id} className="p-4">
        <div className="mb-3">
          <p className="text-sm font-semibold text-gray-800">{item.material_nombre}</p>
          {item.material_sku && (
            <p className="text-xs text-gray-400">{item.material_sku}</p>
          )}
        </div>

        <div className="grid grid-cols-3 gap-2 mb-3 text-center">
          <div>
            <p className="text-[10px] text-gray-400 uppercase">OT Total</p>
            <p className="text-sm font-bold text-gray-700">{item.total_ot}</p>
          </div>
          <div>
            <p className="text-[10px] text-gray-400 uppercase">Ya pedido</p>
            <p className="text-sm font-bold text-gray-700">{item.total_pedido}</p>
          </div>
          <div>
            <p className="text-[10px] text-gray-400 uppercase">Pendiente</p>
            <p className={`text-sm font-bold ${item.pendiente > 0 ? 'text-green-600' : 'text-gray-400'}`}>
              {item.pendiente}
            </p>
          </div>
        </div>

        {item.excede_ot && (
          <div className="flex items-center gap-1 mb-2 text-xs text-orange-600">
            <AlertTriangle className="w-3 h-3" />
            Excede OT
          </div>
        )}

        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 font-medium">Pedir ahora:</span>
          <button
            className="px-2 py-0.5 text-[10px] font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100"
            onClick={() => setMatQty(item.material_id, item.pendiente)}
          >
            Total
          </button>
          <input
            type="number"
            min={0}
            step="any"
            value={qty || ''}
            onChange={(e) => handleMatQtyChange(item, Number(e.target.value) || 0)}
            onBlur={() => handleMatQtyBlur(item)}
            placeholder="0"
            className="w-24 px-2 py-1 text-sm border border-gray-200 rounded text-center focus:ring-2 focus:ring-[#3c98ff]/30 focus:border-[#3c98ff] outline-none"
          />
          <span className="text-xs text-gray-400">{item.material_unidad}</span>
        </div>
        {justifications.has(item.material_id) && (
          <p className="text-xs text-orange-600 mt-1.5 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            Exceso justificado
          </p>
        )}
        {qty > item.pendiente && !justifications.has(item.material_id) && qty > 0 && (
          <p className="text-xs text-orange-600 mt-1.5 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            Excede la OT — requiere justificación
          </p>
        )}
      </Card>
    );
  };

  return (
    <div className="pb-32 space-y-4 fade-up">
      {/* Header */}
      <div className="flex items-center gap-3 pt-2 px-1">
        <Button variant="ghost" size="sm" onClick={() => navigate('/despachos')}>
          <ArrowLeft className="w-4 h-4 mr-1" />
          Compras
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold text-gray-800 truncate">
            Pedido #{pedido.numero_orden != null ? String(pedido.numero_orden) : pedido.id.slice(-6).toUpperCase()}
          </h1>
          <p className="text-xs text-gray-500">{pedido.created_by.nombre}</p>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
          pedido.estado === 'pendiente' ? 'bg-yellow-100 text-yellow-800'
          : pedido.estado === 'despachado_parcial' ? 'bg-blue-100 text-blue-800'
          : pedido.estado === 'cancelado' ? 'bg-gray-100 text-gray-500'
          : 'bg-green-100 text-green-800'
        }`}>
          {pedido.estado === 'pendiente' ? 'Pendiente'
            : pedido.estado === 'despachado_parcial' ? 'Despachado'
            : pedido.estado === 'cancelado' ? 'Cancelado'
            : pedido.estado}
        </span>
        {pedido.estado !== 'cancelado' && canManageAdicionales && (
          <button
            className="p-1 text-red-400 hover:text-red-600"
            title="Cancelar pedido"
            onClick={() => setShowCancelModal(true)}
          >
            <XCircle className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Residente context */}
      {(residenteNota || customDescriptions.length > 0) && (
        <Card className="mx-1 p-3 bg-amber-50 border border-amber-200">
          <p className="text-xs font-medium text-amber-700 mb-1">Nota del residente</p>
          {residenteNota && <p className="text-sm text-amber-800">{residenteNota}</p>}
          {customDescriptions.map((item) => (
            <p key={item.id} className="text-sm text-amber-800">&bull; {item.nombre_custom}</p>
          ))}
        </Card>
      )}

      {/* Maestro selector for PDF */}
      {maestrosDelProyecto && maestrosDelProyecto.length > 0 && (
        <Card className="mx-1 p-3">
          <label className="block text-xs font-medium text-gray-500 uppercase mb-1">
            Contratista (para PDF)
          </label>
          <select
            value={selectedMaestroId}
            onChange={(e) => setSelectedMaestroId(e.target.value)}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#3c98ff]/30 focus:border-[#3c98ff] outline-none"
          >
            <option value="">— Seleccionar maestro —</option>
            {maestrosDelProyecto.map((m) => (
              <option key={m.maestro_id} value={m.maestro_id}>{m.nombre}</option>
            ))}
          </select>
        </Card>
      )}

      {/* Main tab: Materiales / Equipos / Insumos */}
      <div className="flex gap-1 mx-1 bg-white/40 rounded-lg p-1">
        {([
          { key: 'materiales' as MainTab, label: 'Materiales' },
          { key: 'equipos' as MainTab, label: 'Equipos' },
          { key: 'insumos' as MainTab, label: 'Insumos' },
        ]).map((tab) => (
          <button
            key={tab.key}
            onClick={() => {
              setMainTab(tab.key);
              setMaterialSearch('');
              setEquipoSearch('');
              setInsumoSearch('');
            }}
            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition ${
              mainTab === tab.key
                ? 'bg-white shadow-sm text-gray-900'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
            {tab.key === 'materiales' && totalMatItems > 0 && (
              <span className="ml-1 text-[10px] bg-blue-100 text-blue-700 rounded-full px-1.5 py-0.5">
                {totalMatItems}
              </span>
            )}
            {tab.key === 'equipos' && totalEqItems > 0 && (
              <span className="ml-1 text-[10px] bg-blue-100 text-blue-700 rounded-full px-1.5 py-0.5">
                {totalEqItems}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── MATERIALES TAB ── */}
      {mainTab === 'materiales' && (
        <>
          {categories.length === 0 ? (
            <Card className="mx-1 p-8 text-center">
              <Package className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No hay materiales en la OT de este proyecto.</p>
              <p className="text-gray-400 text-xs mt-1">Verifica que el proyecto tiene una OT activa.</p>
            </Card>
          ) : (
            <>
              {/* Category tabs */}
              <div className="flex gap-1 overflow-x-auto px-1 pb-1 scrollbar-hide">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => !materialSearch && setActiveCategory(cat)}
                    className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition ${
                      materialSearch
                        ? 'bg-white text-gray-400 border border-gray-200 cursor-default'
                        : activeCategory === cat
                        ? 'bg-[#3c98ff] text-white shadow-sm'
                        : 'bg-white text-gray-600 border border-gray-200'
                    }`}
                  >
                    {CATEGORY_LABELS[cat] ?? cat}
                  </button>
                ))}
              </div>

              {/* Material search bar */}
              <div className="relative px-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  value={materialSearch}
                  onChange={(e) => setMaterialSearch(e.target.value)}
                  placeholder="Buscar material por nombre o SKU..."
                  className="w-full rounded-lg border border-gray-200 pl-9 pr-8 py-2 text-sm bg-white focus:ring-2 focus:ring-[#3c98ff]/30 focus:border-[#3c98ff] outline-none"
                />
                {materialSearch && (
                  <button
                    type="button"
                    onClick={() => setMaterialSearch('')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Material rows */}
              <div className="space-y-3 px-1">
                {searchedMaterialGroups !== null ? (
                  searchedMaterialGroups.length > 0 ? (
                    searchedMaterialGroups.map(({ cat, items }) => (
                      <div key={cat}>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-1 mb-2">
                          {CATEGORY_LABELS[cat] ?? cat}
                        </p>
                        {items.map((item) => renderMaterialCard(item))}
                      </div>
                    ))
                  ) : (
                    <Card className="p-8 text-center">
                      <p className="text-gray-400 text-sm">Sin resultados para "{materialSearch}"</p>
                    </Card>
                  )
                ) : (
                  balanceByCategory(activeCategory).map((item) => renderMaterialCard(item))
                )}
              </div>
            </>
          )}

          {/* ── Materiales Adicionales section ── */}
          <div className="px-1 mt-4 mb-2">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Materiales Adicionales
              </p>
              {canManageAdicionales && pedido.estado !== 'cancelado' && (
                <Button size="sm" variant="secondary" onClick={() => setShowAddMatAdicional(true)}>
                  <Plus className="w-3 h-3 mr-1" />
                  Agregar
                </Button>
              )}
            </div>
            {pedido.items.filter((i) => i.es_material_adicional).length === 0 ? (
              <p className="text-xs text-gray-400 py-3 text-center">Sin materiales adicionales.</p>
            ) : (
              <div className="space-y-2">
                {pedido.items.filter((i) => i.es_material_adicional).map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-2 p-3 bg-orange-50 border border-orange-100 rounded-lg"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] px-1.5 py-0.5 bg-orange-200 text-orange-700 rounded font-medium">
                          ADICIONAL
                        </span>
                        <p className="text-sm font-medium truncate">{item.material_nombre}</p>
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {item.material_sku && `${item.material_sku} · `}
                        {item.cantidad} {item.material_unidad}
                      </p>
                    </div>
                    {(item.dispatched_qty ?? 0) === 0 && canManageAdicionales && pedido.estado !== 'cancelado' && (
                      <button
                        onClick={() =>
                          deleteItem.mutate({ itemId: item.id, pedidoId: pedidoId ?? '' })
                        }
                        className="text-red-400 hover:text-red-600 hover:bg-red-50 transition rounded p-1.5 flex-shrink-0"
                        title="Eliminar material adicional"
                        disabled={deleteItem.isPending}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* ── EQUIPOS TAB ── */}
      {mainTab === 'equipos' && (
        <div className="space-y-3 px-1">
          {/* Equipo search bar */}
          {activeEquipos.length > 0 && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={equipoSearch}
                onChange={(e) => setEquipoSearch(e.target.value)}
                placeholder="Buscar equipo por nombre o código..."
                className="w-full rounded-lg border border-gray-200 pl-9 pr-8 py-2 text-sm bg-white focus:ring-2 focus:ring-[#3c98ff]/30 focus:border-[#3c98ff] outline-none"
              />
              {equipoSearch && (
                <button
                  type="button"
                  onClick={() => setEquipoSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
          {activeEquipos.length === 0 ? (
            <Card className="p-8 text-center">
              <Wrench className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No hay equipos en inventario.</p>
            </Card>
          ) : filteredEquipos.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-gray-400 text-sm">Sin resultados para "{equipoSearch}"</p>
            </Card>
          ) : (
            filteredEquipos.map((equipo) => {
              const qty = equipoQty.get(equipo.id) ?? 0;
              return (
                <Card key={equipo.id} className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-sm font-semibold text-gray-800">{equipo.nombre}</p>
                      {equipo.codigo && <p className="text-xs text-gray-400">{equipo.codigo}</p>}
                      {equipo.descripcion && (
                        <p className="text-xs text-gray-400 mt-0.5">{equipo.descripcion}</p>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0 ml-3">
                      <p className="text-xs text-gray-400">Disponible</p>
                      <p className={`text-sm font-bold ${equipo.stock_disponible > 0 ? 'text-green-600' : 'text-red-500'}`}>
                        {equipo.stock_disponible}
                        <span className="text-gray-400 font-normal"> / {equipo.stock_total}</span>
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 font-medium">Solicitar:</span>
                    <button
                      className="px-2 py-0.5 text-[10px] font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100"
                      onClick={() => setEqQty(equipo.id, equipo.stock_disponible)}
                    >
                      Total
                    </button>
                    <input
                      type="number"
                      min={0}
                      max={equipo.stock_disponible}
                      step={1}
                      value={qty || ''}
                      onChange={(e) => setEqQty(equipo.id, Number(e.target.value) || 0)}
                      placeholder="0"
                      className="w-24 px-2 py-1 text-sm border border-gray-200 rounded text-center focus:ring-2 focus:ring-[#3c98ff]/30 focus:border-[#3c98ff] outline-none"
                    />
                    <span className="text-xs text-gray-400">unid</span>
                  </div>
                </Card>
              );
            })
          )}
        </div>
      )}

      {/* ── INSUMOS TAB ── */}
      {mainTab === 'insumos' && (
        <div className="space-y-4 px-1">
          {insumosData.length === 0 ? (
            <Card className="p-8 text-center">
              <Beaker className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No hay insumos en el catálogo.</p>
            </Card>
          ) : (
            <>
              {/* Insumo search bar */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  value={insumoSearch}
                  onChange={(e) => setInsumoSearch(e.target.value)}
                  placeholder="Buscar insumo..."
                  className="w-full rounded-lg border border-gray-200 pl-9 pr-8 py-2 text-sm bg-white focus:ring-2 focus:ring-[#3c98ff]/30 focus:border-[#3c98ff] outline-none"
                />
                {insumoSearch && (
                  <button
                    type="button"
                    onClick={() => setInsumoSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              {(['PINTURA', 'IMPERMEABILIZACION', 'GYPSUM'] as const).map((cat) => {
                const catInsumos = insumosData
                  .filter((i) => i.isActive && i.categoria === cat)
                  .filter((i) =>
                    !insumoSearch.trim() ||
                    i.detalle.toLowerCase().includes(insumoSearch.toLowerCase()) ||
                    i.presentacion.toLowerCase().includes(insumoSearch.toLowerCase()),
                  );
                if (catInsumos.length === 0) return null;
                return (
                  <div key={cat}>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-1 mb-2">
                      {cat === 'IMPERMEABILIZACION' ? 'Impermeabilización' : cat.charAt(0) + cat.slice(1).toLowerCase()}
                    </h3>
                    <div className="space-y-2">
                      {catInsumos.sort((a, b) => a.detalle.localeCompare(b.detalle, 'es')).map((ins) => {
                        const qty = insumoQty.get(ins.id) ?? 0;
                        const existingItem = pedido.items.find((i) => i.insumo_id === ins.id);
                        const isDispatched = (existingItem?.dispatched_qty ?? 0) > 0;
                        const canEdit = canManageAdicionales && pedido.estado !== 'cancelado' && !isDispatched;
                        return (
                          <Card key={ins.id} className="p-3">
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-800 leading-tight">{ins.detalle}</p>
                                <p className="text-xs text-gray-400 mt-0.5">
                                  {ins.presentacion} · ${ins.precioUnitario.toFixed(2)}
                                  {qty > 0 && (
                                    <span className="text-purple-600 font-medium">
                                      {' · '}subtotal ${(ins.precioUnitario * qty).toFixed(2)}
                                    </span>
                                  )}
                                </p>
                                {isDispatched && (
                                  <p className="text-xs text-blue-500 mt-0.5">
                                    Despachado: {existingItem?.dispatched_qty} unid
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-1.5 flex-shrink-0">
                                <input
                                  type="number"
                                  min={0}
                                  step="any"
                                  value={qty || ''}
                                  onChange={(e) => canEdit && setInsumoQty(ins.id, Number(e.target.value) || 0)}
                                  disabled={!canEdit}
                                  placeholder="0"
                                  className="w-20 px-2 py-1 text-sm border border-gray-200 rounded text-center focus:ring-2 focus:ring-purple-300 focus:border-purple-400 outline-none disabled:bg-gray-50 disabled:text-gray-400"
                                />
                                <span className="text-xs text-gray-400">unid</span>
                              </div>
                            </div>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {insumoSearch.trim() &&
                (['PINTURA', 'IMPERMEABILIZACION', 'GYPSUM'] as const).every((cat) =>
                  insumosData.filter((i) => i.isActive && i.categoria === cat).filter((i) =>
                    i.detalle.toLowerCase().includes(insumoSearch.toLowerCase()) ||
                    i.presentacion.toLowerCase().includes(insumoSearch.toLowerCase()),
                  ).length === 0,
                ) && (
                <Card className="p-8 text-center">
                  <p className="text-gray-400 text-sm">Sin resultados para "{insumoSearch}"</p>
                </Card>
              )}

              {/* Total + Save */}
              {(() => {
                const totalInsumos = Array.from(insumoQty.entries()).reduce((acc, [insumoId, qty]) => {
                  const ins = insumosData.find((i) => i.id === insumoId);
                  return acc + (ins?.precioUnitario ?? 0) * qty;
                }, 0);
                return totalInsumos > 0 ? (
                  <div className="flex justify-between items-center py-2 border-t border-gray-100 px-1">
                    <span className="text-sm font-medium text-gray-600">Total insumos</span>
                    <span className="text-sm font-bold text-purple-700">${totalInsumos.toFixed(2)}</span>
                  </div>
                ) : null;
              })()}

              {canManageAdicionales && pedido.estado !== 'cancelado' && !insumosSaved && (
                <Button
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  onClick={handleSaveInsumos}
                  isLoading={addItem.isPending || deleteItem.isPending}
                >
                  <Save className="w-4 h-4 mr-2" />
                  Guardar insumos
                </Button>
              )}
            </>
          )}
        </div>
      )}

      {/* Sticky bottom bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-gray-200 p-4 safe-area-inset-bottom z-30">
        <div className="max-w-2xl mx-auto flex gap-3 flex-wrap">
          <Button
            className="flex-1"
            onClick={handleSave}
            isLoading={updateItems.isPending}
            disabled={totalItems === 0 && !hasAdicionalesOnly && !hasInsumosOnly && !hasPreviousMaterialEquipo}
          >
            <Save className="w-4 h-4 mr-2" />
            Guardar ({totalItems} ítem{totalItems !== 1 ? 's' : ''})
          </Button>
          <Button
            variant="secondary"
            onClick={() => pedidoId && ordenPdf.mutate({ pedidoId, maestroId: selectedMaestroId || null })}
            isLoading={ordenPdf.isPending}
            title={selectedMaestroId ? undefined : 'Selecciona un maestro para incluirlo como contratista'}
          >
            <FileText className="w-4 h-4 mr-2" />
            PDF
          </Button>
          {saved && (
            <Button
              className="flex-1 bg-green-600 hover:bg-green-700"
              onClick={() => setShowDispatch(true)}
            >
              <Truck className="w-4 h-4 mr-2" />
              Despachar
            </Button>
          )}
        </div>
      </div>

      {/* Excess warning modal */}
      {excessModal && (
        <Modal isOpen onClose={() => setExcessModal(null)} title="Alerta de Exceso" size="sm">
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
              <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-orange-800">{excessModal.nombre}</p>
                <p className="text-xs text-orange-700 mt-1">
                  Solicitando <strong>{excessModal.newQty}</strong> unidades pero el pendiente de OT es{' '}
                  <strong>{excessModal.pendiente}</strong>. Se excede en{' '}
                  <strong>{(excessModal.newQty - excessModal.pendiente).toFixed(2)}</strong> unidades.
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Justificación requerida <span className="text-red-500">*</span>
              </label>
              <textarea
                value={excessJustification}
                onChange={(e) => setExcessJustification(e.target.value)}
                placeholder="Explica por qué se necesita esta cantidad adicional (mínimo 10 caracteres)..."
                rows={3}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400 outline-none"
              />
              {excessJustification.trim().length > 0 && excessJustification.trim().length < 10 && (
                <p className="text-xs text-red-500 mt-1">Mínimo 10 caracteres</p>
              )}
            </div>

            <div className="flex gap-2 justify-end">
              <Button variant="secondary" onClick={() => setExcessModal(null)}>Cancelar</Button>
              <Button
                className="bg-orange-500 hover:bg-orange-600"
                onClick={handleExcessConfirm}
                disabled={excessJustification.trim().length < 10}
              >
                Confirmar exceso
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Dispatch modal */}
      {showDispatch && pedidoId && (
        <DispatchModal
          pedidoId={pedidoId}
          onClose={() => setShowDispatch(false)}
          onSuccess={() => navigate('/despachos')}
        />
      )}

      {/* Add Material Adicional Modal */}
      {showAddMatAdicional && (
        <Modal
          isOpen
          onClose={() => {
            setShowAddMatAdicional(false);
            setAddMatId('');
            setAddMatName('');
            setAddMatSearch('');
            setAddMatQty(0);
          }}
          title="Agregar Material Adicional"
          size="sm"
        >
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buscar material del catálogo
              </label>
              {addMatId ? (
                <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <span className="flex-1 text-sm font-medium text-blue-800 truncate">{addMatName}</span>
                  <button
                    type="button"
                    onClick={() => { setAddMatId(''); setAddMatName(''); setAddMatSearch(''); }}
                    className="text-blue-400 hover:text-blue-600 text-lg leading-none flex-shrink-0"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    value={addMatSearch}
                    onChange={(e) => setAddMatSearch(e.target.value)}
                    placeholder="Buscar por nombre o SKU..."
                    autoFocus
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#3c98ff]/30 focus:border-[#3c98ff] outline-none"
                  />
                  {matSearchResults.length > 0 && (
                    <div className="mt-1 border border-gray-200 rounded-lg overflow-y-auto max-h-48 bg-white shadow-sm">
                      {matSearchResults.map((mat) => (
                        <button
                          key={mat.id}
                          type="button"
                          onClick={() => { setAddMatId(mat.id); setAddMatName(mat.nombre); }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-gray-100 last:border-0 transition"
                        >
                          <span className="font-medium">{mat.nombre}</span>
                          <span className="text-gray-400 text-xs ml-1">
                            ({mat.unidad}){mat.sku ? ` — ${mat.sku}` : ''}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                  {addMatSearch && matSearchResults.length === 0 && (
                    <p className="text-xs text-gray-400 mt-1 px-1">
                      Sin resultados para "{addMatSearch}"
                    </p>
                  )}
                </>
              )}
            </div>
            <Input
              label="Cantidad"
              type="number"
              min="0.01"
              step="any"
              value={addMatQty || ''}
              onChange={(e) => setAddMatQty(Number(e.target.value) || 0)}
              placeholder="0"
            />
            <div className="flex gap-2 justify-end">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowAddMatAdicional(false);
                  setAddMatId('');
                  setAddMatName('');
                  setAddMatSearch('');
                  setAddMatQty(0);
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={async () => {
                  if (!addMatId || addMatQty <= 0) {
                    toast('error', 'Selecciona un material y una cantidad válida');
                    return;
                  }
                  try {
                    await addItem.mutateAsync({
                      pedidoId: pedidoId ?? '',
                      data: { tipo: 'material_adicional', materialId: addMatId, cantidad: addMatQty },
                    });
                    toast('success', 'Material adicional agregado');
                    setShowAddMatAdicional(false);
                    setAddMatId('');
                    setAddMatName('');
                    setAddMatSearch('');
                    setAddMatQty(0);
                  } catch {
                    // handled by api-client
                  }
                }}
                isLoading={addItem.isPending}
                disabled={!addMatId || addMatQty <= 0}
              >
                Agregar
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Cancel pedido confirmation */}
      <Modal isOpen={showCancelModal} onClose={() => setShowCancelModal(false)} title="Cancelar Pedido" size="sm">
        <div className="space-y-4">
          <div className="flex items-center gap-2 p-3 bg-yellow-50 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
            <p className="text-sm text-yellow-800">
              {pedido && ['despachado_parcial', 'despachado_total'].includes(pedido.estado)
                ? 'Este pedido ya fue despachado. Al cancelarlo, los equipos despachados serán devueltos al inventario y los costos de insumos adicionales serán revertidos.'
                : 'Esta acción no se puede deshacer. El pedido quedará cancelado permanentemente.'}
            </p>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={() => setShowCancelModal(false)}>Volver</Button>
            <Button variant="danger" onClick={handleCancelPedido} isLoading={cancelPedido.isPending}>
              Cancelar pedido
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ── Inline Dispatch Modal ─────────────────────────────────────────

function DispatchModal({
  pedidoId,
  onClose,
  onSuccess,
}: {
  pedidoId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { data: pedido, isLoading } = usePedidoDetail(pedidoId);
  const createDespacho = useCreateDespacho();
  const [fechaDespacho, setFechaDespacho] = useState(new Date().toISOString().split('T')[0]);
  const [quantities, setQuantities] = useState<Map<string, number>>(new Map());
  const [notas, setNotas] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // Pre-fill with remaining quantities (cantidad - already dispatched)
  useEffect(() => {
    if (!pedido) return;
    const map = new Map<string, number>();
    for (const item of pedido.items) {
      // Include: material items that are NOT mdo, equipo items, and insumo items
      if (item.material_id && item.material_categoria === 'mdo') continue;
      if (!item.material_id && !item.equipo_id && !item.insumo_id) continue; // skip nombre_custom items only
      const remaining = item.cantidad - (item.dispatched_qty ?? 0);
      if (remaining > 0) map.set(item.id, remaining);
    }
    setQuantities(map);
  }, [pedido?.id]);

  const handleQuantityChange = (itemId: string, value: number) => {
    const next = new Map(quantities);
    if (value <= 0) next.delete(itemId);
    else next.set(itemId, value);
    setQuantities(next);
  };

  const handleSubmit = async () => {
    const items = Array.from(quantities.entries())
      .filter(([, qty]) => qty > 0)
      .map(([pedidoItemId, cantidadDespachada]) => ({ pedidoItemId, cantidadDespachada }));

    if (items.length === 0) {
      toast('error', 'Debe ingresar al menos una cantidad');
      return;
    }

    try {
      await createDespacho.mutateAsync({
        pedidoId,
        data: { fechaDespacho, notas: notas.trim() || null, items },
      });
      setSubmitted(true);
      toast('success', 'Despacho registrado exitosamente');
      onSuccess();
    } catch {
      // handled by api-client
    }
  };

  // Only show non-mdo material/equipo/insumo items that still have remaining quantity to dispatch
  const dispatchableItems = (pedido?.items ?? []).filter((i) => {
    if (!((i.material_id && i.material_categoria !== 'mdo') || i.equipo_id || i.insumo_id)) return false;
    const remaining = i.cantidad - (i.dispatched_qty ?? 0);
    return remaining > 0;
  });

  return (
    <Modal isOpen onClose={onClose} title="Registrar Despacho" size="lg">
      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : (
        <div className="space-y-4">
          <Input
            label="Fecha de despacho"
            type="date"
            value={fechaDespacho}
            onChange={(e) => setFechaDespacho(e.target.value)}
          />

          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Ítems a despachar</p>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {dispatchableItems.map((item) => {
                const remaining = item.cantidad - (item.dispatched_qty ?? 0);
                return (
                  <div key={item.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {item.insumo_detalle ?? item.material_nombre ?? item.equipo_nombre ?? 'Sin nombre'}
                      </p>
                      <p className="text-xs text-gray-400">
                        Pendiente: {remaining} {item.material_unidad ?? 'unid'}
                        {(item.dispatched_qty ?? 0) > 0 && (
                          <span className="ml-1 text-green-600">(ya despachado: {item.dispatched_qty})</span>
                        )}
                      </p>
                    </div>
                    <button
                      className="px-2 py-1 text-[10px] font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100"
                      onClick={() => handleQuantityChange(item.id, remaining)}
                    >
                      Total
                    </button>
                    <div className="w-24">
                      <input
                        type="number"
                        min={0}
                        max={remaining}
                        step="any"
                        placeholder="0"
                        value={quantities.get(item.id) ?? ''}
                        onChange={(e) => handleQuantityChange(item.id, Number(e.target.value) || 0)}
                        className="w-full px-2 py-1 text-sm border border-gray-200 rounded text-center"
                      />
                    </div>
                  </div>
                );
              })}
              {dispatchableItems.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-4">
                  Todos los ítems ya fueron despachados en su totalidad.
                </p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
            <textarea
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              placeholder="Notas sobre el despacho..."
              rows={2}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none"
            />
          </div>

          <div className="flex gap-2 justify-end pt-2">
            <Button variant="secondary" onClick={onClose}>Cancelar</Button>
            <Button
              onClick={handleSubmit}
              isLoading={createDespacho.isPending}
              disabled={dispatchableItems.length === 0 || submitted}
            >
              <Truck className="w-4 h-4 mr-1" />
              Registrar Despacho
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
